import { useState, useMemo, useEffect } from "react";
import { Building2, Shield, Users } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  Button,
} from "@nexus/ui";
import type { UserAccessEntry, AccessGroupItem } from "@/features/identity-hub/api/identity-hub.api";

interface BulkGrantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUserIds: string[];
  accessByUser: Map<string, UserAccessEntry[]>;
  allTenants: Array<{ id: string; name: string }>;
  groupsByTenant: Map<string, AccessGroupItem[]>;
  onApply: (grants: Array<{ userId: string; accessId: string; tenantId: string }>) => void;
  isPending: boolean;
}

export function BulkGrantDialog({
  open, onOpenChange, selectedUserIds, accessByUser,
  allTenants, groupsByTenant, onApply, isPending,
}: BulkGrantDialogProps) {
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");

  const rolesForOrg = useMemo(
    () => (selectedOrgId ? (groupsByTenant.get(selectedOrgId) ?? []) : []),
    [selectedOrgId, groupsByTenant],
  );

  // Auto-select single role; clear when org changes
  useEffect(() => {
    if (!selectedOrgId) { setSelectedRoleId(""); return; }
    const roles = groupsByTenant.get(selectedOrgId) ?? [];
    setSelectedRoleId(roles.length === 1 ? roles[0].id : "");
  }, [selectedOrgId, groupsByTenant]);

  // Reset on close
  useEffect(() => {
    if (!open) { setSelectedOrgId(""); setSelectedRoleId(""); }
  }, [open]);

  // Skip users that already have a role in the selected org (1 role per user per org)
  const { eligible, skipped } = useMemo(() => {
    if (!selectedOrgId || !selectedRoleId) return { eligible: [] as string[], skipped: 0 };
    const eligible: string[] = [];
    let skipped = 0;
    for (const userId of selectedUserIds) {
      const alreadyInOrg = (accessByUser.get(userId) ?? []).some((e) => e.access.tenantId === selectedOrgId);
      if (alreadyInOrg) { skipped++; continue; }
      eligible.push(userId);
    }
    return { eligible, skipped };
  }, [selectedUserIds, accessByUser, selectedOrgId, selectedRoleId]);

  const handleSubmit = () => {
    if (!selectedOrgId || !selectedRoleId || eligible.length === 0) return;
    onApply(eligible.map((userId) => ({ userId, accessId: selectedRoleId, tenantId: selectedOrgId })));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Assign role</DialogTitle>
          <DialogDescription>
            Select an org and role. Users already in the org are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-1">
          <div className="flex flex-col gap-1.5">
            <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
              <Building2 className="h-3 w-3" /> Organization
            </span>
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">Select organization…</option>
              {allTenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {selectedOrgId && (
            <div className="flex flex-col gap-1.5">
              <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                <Shield className="h-3 w-3" /> Role
              </span>
              {rolesForOrg.length === 0 ? (
                <p className="text-xs text-muted-foreground">No roles available for this organization.</p>
              ) : rolesForOrg.length === 1 ? (
                <div className="flex h-9 items-center rounded-md border border-border bg-muted/30 px-2.5 text-sm text-foreground">
                  {rolesForOrg[0].name}
                </div>
              ) : (
                <select
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  className="h-9 rounded-md border border-border bg-background px-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="">Select role…</option>
                  {rolesForOrg.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {selectedOrgId && selectedRoleId && (
            <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              <Users className="h-3.5 w-3.5 shrink-0" />
              <span>
                <strong className="text-foreground">{eligible.length}</strong> of{" "}
                <strong className="text-foreground">{selectedUserIds.length}</strong> users will be assigned
                {skipped > 0 && <> · {skipped} already in org</>}
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={eligible.length === 0 || isPending}
            onClick={handleSubmit}
          >
            {isPending
              ? "Applying…"
              : eligible.length > 0
                ? `Assign to ${eligible.length} user${eligible.length !== 1 ? "s" : ""}`
                : "Assign"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
